/**
 * US phone-number formatting for form inputs (apps/ordo).
 *
 * The profile "Cell phone" field masks input live as "313-234-2344"
 * (Sheila 7/21 QA, item 4). We store the dashed string the user sees —
 * the backend phone column accepts digits / spaces / dashes / parens /
 * `+` at 6–20 chars (see server `routes/me-helpers.ts`), so a dashed
 * 10-digit US number round-trips without a separate normalization step.
 *
 * ASSUMED: US 10-digit numbers. International / E.164 values are out of
 * scope for this mask; a previously-stored non-US value is left as-is
 * on the wire until the user actively edits the field.
 */

/** Strip everything but digits. */
export function phoneDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/**
 * Live input mask: digits only, capped at 10, formatted "313-234-2344".
 * Partial input formats progressively ("313", "313-234", "313-234-2").
 */
export function formatUsPhone(value: string): string {
  const digits = phoneDigits(value).slice(0, 10)
  if (digits.length <= 3) {
    return digits
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

/** True once the value carries a complete 10-digit US number. */
export function isCompleteUsPhone(value: string): boolean {
  return phoneDigits(value).length === 10
}
